import { readItemField } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readCreatureStatuses } from './creature-status-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readItemNumericSlot } from './enchantment-template';
import { phpFloatToInteger } from './php-integer-arithmetic';
import { phpJsonTruthy } from './php-json-values';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createLayerEntriesReader } from './layer-entries-reader';
import { readNodeItemId, readOverrideEntryAbilityId } from './layer-cell-readers';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const lowerAscii = (value: string): string => value.replace(/[A-Z]/g, letter => letter.toLowerCase());
function circleComesFirst(left: string, right: string): boolean {
    // strcasecmp compares ASCII-folded UTF-8 bytes, never numeric strings or
    // JavaScript UTF-16 code units. Valid JSON Unicode has the same code-point order.
    const a = Array.from(lowerAscii(left)), b = Array.from(lowerAscii(right));
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] !== b[i]) return a[i]!.codePointAt(0)! < b[i]!.codePointAt(0)!;
    }
    return a.length < b.length;
}

export function isInventoryInstanceEnchanted(row: ItemFieldRow): boolean {
    if (phpJsonTruthy(own(own(row, 'inventory'), 'enchantment_template_id'))) return true;
    const flat = own(row, 'enchantment_template_id');
    if (phpJsonTruthy(arrayLike(flat) ? own(flat, '0') : flat)) return true;
    return own(own(row, 'computed'), 'enchantment') != null;
}

export interface InventoryMagicSignature { circle_id: string | null; level: number | null }
export interface InventoryMagicReaders {
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow, name: string) => ItemFieldRow;
    fullItem: (id: string) => ItemFieldRow | null;
    statuses: (row: ItemFieldRow) => ItemFieldRow;
    itemField: (row: ItemFieldRow | null, key: string) => unknown;
}

/** Definition metadata is memoized by the exact requested ID for this compiler
 * lifetime. Candidate order, null-level sentinel and category ties follow PHP.
 * This computes the descriptor only; it does not evaluate visibility or emitters.
 */
export function createInventoryMagicSignatureResolver(readers: InventoryMagicReaders):
    (row: ItemFieldRow) => InventoryMagicSignature | null {
    type Meta = { level: bigint | null; circle_id: string | null };
    const memo = new Map<string, Meta>();
    const metadata = (id: string): Meta => {
        if (id === '') return { level: null, circle_id: null };
        const cached = memo.get(id); if (cached) return cached;
        const item = readers.fullItem(id);
        let level = readers.layerValue(item, 'spell_level');
        if (level === null || level === '') level = readers.layerValue(item, 'menta_level');
        const circle = readers.layerValue(item, 'spell_circle');
        const meta = { level: level === null || level === '' ? null : phpFloatToInteger(readItemNumericSlot(level, 0)),
            circle_id: typeof circle === 'string' && circle !== '' ? circle : null };
        memo.set(id, meta); return meta;
    };
    const candidates = (row: ItemFieldRow): [string, number][] => {
        const out: [string, number][] = [];
        for (const [layers, category] of [[['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell'], 1],
            [['curse', 'curse_known'], 2]] as const) {
            for (const name of layers) for (const entry of Object.values(readers.layerEntries(row, name))) {
                const id = readLayerListEntryUuid(entry); if (id !== '') out.push([id, category]);
            }
        }
        return out;
    };
    return row => {
        let best: (Meta & { category: number }) | undefined;
        const consider = (meta: Meta, category: number): void => {
            const candidate = { ...meta, category };
            if (!best) { best = candidate; return; }
            const currentLevel = meta.level ?? -1n, bestLevel = best.level ?? -1n;
            if (currentLevel !== bestLevel) { if (currentLevel > bestLevel) best = candidate; return; }
            if (category !== best.category) { if (category > best.category) best = candidate; return; }
            if ((meta.circle_id !== null) !== (best.circle_id !== null)) { if (meta.circle_id !== null) best = candidate; return; }
            if (meta.circle_id !== null && best.circle_id !== null && circleComesFirst(meta.circle_id, best.circle_id)) best = candidate;
        };
        const addLayers = (item: ItemFieldRow): void => { for (const [id, category] of candidates(item)) consider(metadata(id), category); };
        addLayers(row);
        const enchantment = own(own(row, 'computed'), 'enchantment'), abilities = own(enchantment, 'abilities');
        if (arrayLike(enchantment) && phpJsonTruthy(abilities) && arrayLike(abilities)) {
            for (const entries of Object.values(abilities)) if (arrayLike(entries)) {
                for (const entry of Object.values(entries)) { const item = own(entry, 'item_arr'); if (arrayLike(item)) addLayers(item); }
            }
        }
        for (const status of Object.values(readers.statuses(row))) {
            if (status !== null && !arrayLike(status)) throw new TypeError('item_field requires a PHP array or null');
            const type = readers.itemField(status, 'item_type') ?? '';
            if (type !== 'spell' && type !== 'curse') continue;
            const id = readers.itemField(status, 'item_id') ?? '';
            if (typeof id === 'string' && id !== '') consider(metadata(id), type === 'curse' ? 2 : 1);
        }
        if (phpFloatToInteger(readItemNumericSlot(readers.layerValue(row, 'persistent_aoe') ?? 0, 0)) === 1n) {
            const circle = readers.layerValue(row, 'spell_circle');
            consider({ level: null, circle_id: typeof circle === 'string' && circle !== '' ? circle : null }, 3);
        }
        if (!best && !isInventoryInstanceEnchanted(row)) return null;
        return { circle_id: best?.circle_id ?? null, level: best?.level == null ? null : Number(best.level) };
    };
}

export interface InventoryMagicDependencies extends ItemFieldDependencies {
    catalogRows: ProviderCatalogRows;
    abilityTypes: readonly string[];
    aggregationStrategy: (name: string) => string;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    fullItem: (id: string) => ItemFieldRow | null;
    trace?: (event: readonly unknown[]) => void;
}
export function createInventoryMagicCompiler(deps: InventoryMagicDependencies): (row: ItemFieldRow) => InventoryMagicSignature | null {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId, resolveCarrierRef: reference });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const lists = createLayerEntriesReader({ nodeItemId, inventoryContext, providers, abilityTypes: deps.abilityTypes,
        aggregationStrategy: name => { const strategy = deps.aggregationStrategy(name); emit(['strategy', name, strategy]); return strategy; },
        overrideEntryAbilityId: entry => { const id = readOverrideEntryAbilityId(entry); emit(['override', id]); return id; } });
    return createInventoryMagicSignatureResolver({ layerValue: scalar.read, layerEntries: lists.read,
        fullItem: id => { emit(['full-item', id]); return deps.fullItem(id); }, statuses: row => readCreatureStatuses(row, 'target', reference),
        itemField: (row, key) => readItemField(row, key, { resolveCarrierRef: reference,
            resolveItemName: id => { emit(['name', id]); if (!deps.resolveItemName) throw new Error('Unavailable item name'); return deps.resolveItemName(id); },
            resolveInventoryIdentity: id => { emit(['inventory-identity', id]); if (!deps.resolveInventoryIdentity) throw new Error('Unavailable inventory identity'); return deps.resolveInventoryIdentity(id); } }),
    });
}
